import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';

// Layouts
import PublicLayout from './layouts/PublicLayout';
import AuthenticatedLayout from './layouts/AuthenticatedLayout';
import ProfileLayout from './layouts/ProfileLayout';
import MembersLayout from './layouts/MembersLayout';

// Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import VerifyEmail from './pages/auth/VerifyEmail';
import VerificationPending from './pages/auth/VerificationPending';
import Dashboard from './pages/Dashboard';
import Galleries from './pages/Galleries';
import Lookups from './pages/admin/Lookups';
import PersonalPageEditor from './pages/PersonalPageEditor';

// Components
import CMSPage from './components/CMSPage';
import Dialog from './components/Dialog';

// CMS Page Wrapper - uses appropriate layout based on auth status
const CMSPageWrapper = () => {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return (
      <AuthenticatedLayout>
        <CMSPage />
      </AuthenticatedLayout>
    );
  }

  return (
    <PublicLayout centered={false}>
      <CMSPage />
    </PublicLayout>
  );
};

// Profile Route - extracts profile ID from URL and passes to ProfileLayout
const ProfileRoute = ({ children }) => {
  const { profileId } = useParams();
  const { accessToken } = useAuthStore();
  const [resolvedProfileId, setResolvedProfileId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProfileId = async () => {
      // Check if profileId is a number (profile ID) or UUID
      if (/^\d+$/.test(profileId)) {
        // It's a numeric ID
        setResolvedProfileId(parseInt(profileId));
        setIsLoading(false);
      } else {
        // It's a UUID, need to look it up
        try {
          const response = await fetch(`/api/profile/by-uuid/${profileId}`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`
            }
          });
          const data = await response.json();
          if (data.success && data.profile) {
            setResolvedProfileId(data.profile.id);
          }
        } catch (error) {
          console.error('Failed to fetch profile by UUID:', error);
        }
        setIsLoading(false);
      }
    };

    if (profileId && accessToken) {
      fetchProfileId();
    }
  }, [profileId, accessToken]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!resolvedProfileId) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <p className="text-primary">Profile not found</p>
      </div>
    );
  }

  return (
    <ProfileLayout profileId={resolvedProfileId}>
      {children}
    </ProfileLayout>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  const { initializeAuth } = useAuthStore();

  // Initialize auth on mount - fetch guest token if not authenticated
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  return (
    <>
      <Dialog />
      <Router>
        <Routes>
        {/* Public Routes */}
        <Route path="/login" element={
          <PublicRoute>
            <PublicLayout>
              <Login />
            </PublicLayout>
          </PublicRoute>
        } />

        <Route path="/register" element={
          <PublicRoute>
            <PublicLayout>
              <Register />
            </PublicLayout>
          </PublicRoute>
        } />

        <Route path="/verify" element={
          <PublicLayout>
            <VerifyEmail />
          </PublicLayout>
        } />

        <Route path="/verification-pending" element={
          <PublicLayout>
            <VerificationPending />
          </PublicLayout>
        } />

        {/* Members Route - Just the members list page */}
        <Route path="/members" element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <CMSPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />

        {/* Profile Routes - All profile pages under /profile/:profileId/* */}
        <Route path="/profile/:profileId/dashboard" element={
          <ProtectedRoute>
            <ProfileRoute>
              <Dashboard />
            </ProfileRoute>
          </ProtectedRoute>
        } />

        <Route path="/profile/:profileId/profile" element={
          <ProtectedRoute>
            <ProfileRoute>
              <CMSPage />
            </ProfileRoute>
          </ProtectedRoute>
        } />

        <Route path="/profile/:profileId/galleries" element={
          <ProtectedRoute>
            <ProfileRoute>
              <Galleries />
            </ProfileRoute>
          </ProtectedRoute>
        } />

        <Route path="/profile/:profileId/page/edit/:id" element={
          <ProtectedRoute>
            <ProfileRoute>
              <PersonalPageEditor />
            </ProfileRoute>
          </ProtectedRoute>
        } />

        <Route path="/profile/:profileId/page/:slug" element={
          <ProtectedRoute>
            <ProfileRoute>
              <CMSPage />
            </ProfileRoute>
          </ProtectedRoute>
        } />

        {/* Admin Routes - Require Authentication + Admin */}
        <Route path="/admin/lookups" element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <Lookups />
            </AuthenticatedLayout>
          </ProtectedRoute>
        } />

        {/* Home page and CMS Pages */}
        <Route path="/" element={<CMSPageWrapper />} />
        <Route path="/:slug" element={<CMSPageWrapper />} />
      </Routes>
      </Router>
    </>
  );
}

export default App;
